import { flatten, isNull, max, noop } from 'lodash/fp';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Document, pdfjs } from 'react-pdf';

import 'react-pdf/dist/esm/Page/AnnotationLayer.css';
import 'react-pdf/dist/esm/Page/TextLayer.css';
import { useAppDispatch, useAppSelector } from '../../../hooks/react-redux';
import { LoadingDots } from '../../shared/loading-dots/LoadingDots';
import { Scrollable } from '../../shared/scrollable/Scrollable';
import styles from './DocumentViewer.module.scss';
import { PDFControlBar } from './PDFControlBar';
import { PageWithObserver } from './PDFPage';
import { PDFSideBar } from './PDFSideBar';
import { getCurrentPdfPage, getSearchResults, getSelectedSearchResult, PDFPageHighlights, PdfPageText, resetPdfSearch, resetPdfText, setCurrentPdfPage, setPdfText, setScheduleStartPage } from './store';

pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.js`;

export interface ReactPdf {
    numPages: number;
}

interface PDFViewerProps {
    pdfUrl: string;
    onLoadSuccess: (pdf: ReactPdf) => void;
    resetNumPages: () => void;
    numPages: number | null;
    width: number;
    selectedPage?: number | null;
    showScheduleStartPage?: boolean;
    setSelectedPage?: (page: number | null) => void;
    scheduleStartPage?: number;
    isSupportDocument?: boolean;
}

export const PDFViewer: React.FC<PDFViewerProps> = ({
    pdfUrl,
    onLoadSuccess,
    numPages,
    resetNumPages,
    width,
    selectedPage = null,
    showScheduleStartPage = false,
    setSelectedPage = noop,
    scheduleStartPage = 1,
    isSupportDocument = false
}) => {
    const dispatch = useAppDispatch();
    const [showThumbnails, setShowThumbnails] = useState<boolean>(true);
    const [visiblePages, setVisiblePages] = useState({});
    const [pdfLoading, setPdfLoading] = useState<boolean>(false);
    const pagesRef = useRef<Map<number, HTMLDivElement> | null>(null);
    const thumbnailsRef = useRef<Map<number, HTMLDivElement> | null>(null);
    const pageArray = useMemo(() => numPages ? new Array(numPages).fill(1).map((_, index) => index + 1) : [], [numPages]);

    const setPageVisibility = useCallback((pageNumber: number, isIntersecting: boolean) => setVisiblePages((prevVisiblePages) => ({
        ...prevVisiblePages,
        [pageNumber]: isIntersecting
    })), []);

    const getPagesMap = useCallback(() => {
        if (!pagesRef.current) {
            pagesRef.current = new Map<number, HTMLDivElement>();
        }
        return pagesRef.current!;
    }, []);

    const setPageRef = useCallback((element: HTMLDivElement | null, pageNumber: number) => {
        const map = getPagesMap();
        if (element) {
            map.set(pageNumber, element);
        } else {
            map.delete(pageNumber);
        }
    }, [getPagesMap]);

    const scrollToPage = useCallback((pageNumber: number) => {
        const pages = getPagesMap();
        const node = pages.get(pageNumber);
        if (node) {
            node.scrollIntoView({ behavior: 'smooth', block: 'start', inline: 'center' });
            setSelectedPage(null);
        }
    }, [getPagesMap, setSelectedPage]);

    const getThumbnailsMap = useCallback(() => {
        if (!thumbnailsRef.current) {
            thumbnailsRef.current = new Map<number, HTMLDivElement>();
        }
        return thumbnailsRef.current!;
    }, []);

    const scrollToThumbnail = useCallback((pageNumber: number) => {
        const thumbnails = getThumbnailsMap();
        const node = thumbnails.get(pageNumber);
        if (node) {
            node.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'center' });
        }
    }, [getThumbnailsMap]);

    const toggleThumbnail = useCallback(() => setShowThumbnails(!showThumbnails), [showThumbnails]);

    const pagesWidth = useMemo(() => showThumbnails ? 0.9 : 1, [showThumbnails]);

    const currentPage = useAppSelector(getCurrentPdfPage);

    const completeLoading = useCallback((pdf: ReactPdf) => {
        setPdfLoading(false);
        onLoadSuccess(pdf);
    }, [onLoadSuccess]);

    const onLoading = useCallback(() => {
        setVisiblePages({});
        resetNumPages();
        setPdfLoading(true);
    }, [resetNumPages]);

    const searchResults = useAppSelector(getSearchResults);
    const selectedSearchResult = useAppSelector(getSelectedSearchResult);

    const searchResult = useMemo(() => selectedSearchResult?.page === currentPage ? selectedSearchResult : null, [selectedSearchResult, currentPage]);

    const setPdfPagesText = useCallback((pdfText: PdfPageText) => dispatch(setPdfText(pdfText)), [dispatch]);

    const currentPageHighlights: number[][] = useMemo(() => {
        const resultsForCurrentPage = searchResults.filter(({ page }) => page === currentPage).map(({ indexes }) => indexes);
        return resultsForCurrentPage.length > 0 ? resultsForCurrentPage : [];
    }, [currentPage, searchResults]);

    const searchTermHighlights: PDFPageHighlights | undefined = useMemo(() => {
        if (currentPageHighlights.length > 0) {
            return { page: currentPage, highlightIndexes: flatten(currentPageHighlights) };
        }
        return undefined;
    }, [currentPage, currentPageHighlights]);

    useEffect(() => {
        const currentPage = max(Object.entries(visiblePages).filter(([, value]) => value).map(([key]) => parseInt(key))) || 1;
        dispatch(setCurrentPdfPage(currentPage));
        dispatch(setScheduleStartPage(scheduleStartPage));
    }, [visiblePages, dispatch, scheduleStartPage]);

    useEffect(() => {
        if (!isNull(selectedPage)) {
            scrollToPage(selectedPage);
        }
    }, [selectedPage, scrollToPage]);

    useEffect(() => () => {
        dispatch(resetPdfSearch());
        dispatch(resetPdfText());
    }, [dispatch, isSupportDocument]);

    return (
        <div className={styles.pdfViewerWrapper}>
            <PDFControlBar
                numPages={numPages}
                pdfLoading={pdfLoading}
                scrollToPage={scrollToPage}
                showScheduleStartPage={showScheduleStartPage}
                setSelectedPage={setSelectedPage}
                scheduleStartPage={scheduleStartPage}
                toggleThumbnail={toggleThumbnail}
                isSupportDocument={isSupportDocument}
            />
            <div className={styles.pdfViewer}>
                <Document
                    file={pdfUrl}
                    onLoadSuccess={completeLoading}
                    onLoadProgress={onLoading}
                    loading={<LoadingDots />}
                    onItemClick={({ pageNumber }) => {
                        scrollToPage(pageNumber);
                    }}
                    className={styles.documentPdfWrapper}
                >
                    <div className={styles.pdfContent}>
                        {showThumbnails &&
                            <PDFSideBar numPages={numPages} width={width} />
                        }
                        <div className={styles.documentWrapper} style={{ width: `${pagesWidth * 100}%` }}>
                            <Scrollable>
                                {pageArray.map(page => (
                                    <PageWithObserver
                                        key={`page_${page}`}
                                        pageNumber={page}
                                        width={width}
                                        setRef={setPageRef}
                                        pagesWidth={pagesWidth}
                                        setPageVisibility={setPageVisibility}
                                        scrollToThumbnail={scrollToThumbnail}
                                        setPdfPagesText={setPdfPagesText}
                                        searchTermHighlights={searchTermHighlights}
                                        selectedSearchResult={searchResult}
                                    />
                                ))}
                            </Scrollable>
                        </div>
                    </div>
                </Document>
            </div>
        </div>
    );
};
